import {
  NEW_END_SENTINEL,
  decisionPathsComplete,
  isBoundaryStep,
  isInsertOverlayFinding,
  requiresBothDecisionPaths,
  stepTypeOf,
  type AccuracyGateFinding,
  type DecisionPathsProposal,
  type FindingStatus,
} from './accuracyGate';

export type FindingAction = 'approve' | 'adjust' | 'reject' | 'keep_flagged';

export interface ApplyFindingOptions {
  action: FindingAction;
  overrides?: {
    name?: string;
    description?: string;
    assignee?: string;
    isDecision?: boolean;
    decisionPaths?: DecisionPathsProposal;
  };
  pendingDeleteIds?: string[];
  sourceHasDiamonds?: boolean;
}

export interface ApplyFindingResult {
  mapData: any;
  pendingDeleteIds: string[];
  status: FindingStatus;
  error?: string;
  createdStepId?: string;
}

function cloneMap(mapData: any) {
  return JSON.parse(JSON.stringify(mapData || { elements: { processes: [] } }));
}

function processesOf(mapData: any): any[] {
  if (!mapData.elements) mapData.elements = {};
  if (!Array.isArray(mapData.elements.processes)) mapData.elements.processes = [];
  return mapData.elements.processes;
}

function flowsOf(mapData: any): any[] {
  if (!mapData.elements) mapData.elements = {};
  if (!Array.isArray(mapData.elements.sequence_flows)) mapData.elements.sequence_flows = [];
  return mapData.elements.sequence_flows;
}

function stepId(step: any): string {
  return String(step?.id ?? '');
}

function findIndexById(processes: any[], id: string | null | undefined): number {
  if (id == null || id === '') return -1;
  const want = String(id);
  return processes.findIndex((p) => String(p.id) === want || String(p.step_number) === want);
}

function uniqueStepId(prefix: string): string {
  return `${prefix}_${Date.now()}_${Math.floor(Math.random() * 100000)}`;
}

export function makeManualStep(form: {
  name: string;
  description?: string;
  assignee?: string;
  stepType?: string;
  isDecision?: boolean;
  decisionPaths?: DecisionPathsProposal | null;
}): any {
  const stepType = form.stepType || (form.isDecision ? 'decision' : 'process');
  const isDecision = stepType === 'decision' || !!form.isDecision;
  const isEnd = stepType === 'end';
  const isStart = stepType === 'start';
  const isAnnotation = stepType === 'annotation';
  return {
    id: uniqueStepId(isEnd ? 'manual_end' : isStart ? 'manual_start' : 'manual_step'),
    name: (form.name || '').trim() || (isEnd ? 'End' : isStart ? 'Start' : 'Step'),
    description: (form.description || '').trim(),
    full_text: (form.description || form.name || '').trim(),
    assignee: form.assignee || '',
    actor: form.assignee || '',
    action_type: isDecision ? 'decision' : isEnd ? 'end' : isStart ? 'start' : isAnnotation ? 'annotation' : 'process',
    step_type: stepType,
    bpmn_type: isDecision ? 'gateway' : isEnd ? 'endEvent' : isStart ? 'startEvent' : isAnnotation ? 'annotation' : 'task',
    event_type: isEnd ? 'endEvent' : isStart ? 'startEvent' : undefined,
    gateway_type: isDecision ? 'exclusive' : undefined,
    is_decision: isDecision,
    classification: isAnnotation ? 'annotation' : undefined,
    decision_paths: isDecision ? (form.decisionPaths || { yes: '', no: '' }) : null,
    connections: [],
    next_steps: [],
    step_number: '',
    step_index: 0,
    position: { x: 100, y: 0 },
    confidence_score: 100,
    source_citations: [],
    processing_method: 'manual_add',
  };
}

function flowMatchesSource(flow: any, step: any): boolean {
  const src = String(flow?.source_step ?? flow?.from ?? '');
  return src === stepId(step) || src === String(step.step_number ?? '');
}

function flowMatchesTarget(flow: any, ref: string): boolean {
  const tgt = String(flow?.target_step ?? flow?.to ?? '');
  return tgt === String(ref);
}

function upsertFlow(flows: any[], fromStep: any, toRef: string, condition: string, isDefault: boolean) {
  if (!toRef) return;
  const existing = flows.find((f) => flowMatchesSource(f, fromStep) && flowMatchesTarget(f, toRef));
  if (existing) {
    existing.condition = condition || '';
    existing.is_default = isDefault;
    return;
  }
  flows.push({
    id: `flow_${stepId(fromStep)}_${toRef}`,
    source_step: stepId(fromStep) || String(fromStep.step_number || ''),
    target_step: toRef,
    condition: condition || '',
    is_default: isDefault,
  });
}

function removeFlowsFrom(flows: any[], fromStep: any, keepTargets?: Set<string>) {
  for (let i = flows.length - 1; i >= 0; i -= 1) {
    if (!flowMatchesSource(flows[i], fromStep)) continue;
    const tgt = String(flows[i].target_step ?? flows[i].to ?? '');
    if (keepTargets && keepTargets.has(tgt)) continue;
    flows.splice(i, 1);
  }
}

function retargetIncoming(flows: any[], fromId: string, toId: string | null) {
  for (const f of flows) {
    const tgt = String(f.target_step ?? f.to ?? '');
    if (tgt !== String(fromId)) continue;
    if (!toId) {
      f._drop = true;
    } else {
      f.target_step = toId;
    }
  }
  for (let i = flows.length - 1; i >= 0; i -= 1) {
    if (flows[i]._drop) flows.splice(i, 1);
  }
}

/** Persist the same decision_paths shape the renderer and pack already read, plus sequence_flows so both branches draw. */
export function applyDecisionToStep(step: any, paths: DecisionPathsProposal | null | undefined, isDecision: boolean) {
  if (!isDecision) {
    step.is_decision = false;
    step.action_type = step.action_type === 'decision' ? 'process' : (step.action_type || 'process');
    if (step.bpmn_type === 'gateway') step.bpmn_type = 'task';
    if (step.step_type === 'decision') step.step_type = 'process';
    step.gateway_type = undefined;
    step.decision_paths = null;
    return step;
  }
  const yes = paths?.yes ? String(paths.yes) : '';
  const no = paths?.no ? String(paths.no) : '';
  const yesLabel = paths?.yes_label || paths?.yes_condition || '';
  const noLabel = paths?.no_label || paths?.no_condition || '';
  step.is_decision = true;
  step.action_type = 'decision';
  step.step_type = 'decision';
  step.bpmn_type = 'gateway';
  step.gateway_type = 'exclusive';
  step.decision_paths = {
    yes,
    no,
    yes_label: yesLabel,
    no_label: noLabel,
    yes_condition: yesLabel,
    no_condition: noLabel,
  };
  return step;
}

export function syncDecisionSequenceFlows(mapData: any, step: any) {
  const flows = flowsOf(mapData);
  const isDecision = stepTypeOf(step) === 'decision' || step.is_decision === true;
  const dp = step.decision_paths;
  if (!isDecision || !dp) return mapData;
  const yes = dp.yes ? String(dp.yes) : '';
  const no = dp.no ? String(dp.no) : '';
  const keep = new Set([yes, no].filter(Boolean));
  removeFlowsFrom(flows, step, keep);
  if (yes) upsertFlow(flows, step, yes, dp.yes_label || dp.yes_condition || '', false);
  if (no) upsertFlow(flows, step, no, dp.no_label || dp.no_condition || '', true);
  return mapData;
}

function resolveNewEnd(mapData: any, afterIndex: number, ref: string | undefined): string {
  if (!ref || ref !== NEW_END_SENTINEL) return ref || '';
  const processes = processesOf(mapData);
  const end = makeManualStep({ name: 'End', stepType: 'end' });
  const insertAt = Math.min(Math.max(afterIndex + 1, 0), processes.length);
  processes.splice(insertAt, 0, end);
  return stepId(end);
}

function insertAfter(processes: any[], afterId: string | null | undefined, newStep: any): number {
  let idx = findIndexById(processes, afterId);
  if (idx < 0) {
    const lastEnd = [...processes].reverse().findIndex((p) => stepTypeOf(p) === 'end');
    if (lastEnd >= 0) {
      idx = processes.length - 1 - lastEnd - 1;
    } else {
      idx = processes.length - 1;
    }
  }
  const insertAt = Math.min(Math.max(idx + 1, 0), processes.length);
  processes.splice(insertAt, 0, newStep);
  return insertAt;
}

function wireLinearInsert(mapData: any, newIndex: number) {
  const processes = processesOf(mapData);
  const flows = flowsOf(mapData);
  const step = processes[newIndex];
  const prev = newIndex > 0 ? processes[newIndex - 1] : null;
  const next = newIndex < processes.length - 1 ? processes[newIndex + 1] : null;
  if (prev && stepTypeOf(prev) !== 'end') {
    prev.next_steps = [stepId(step)];
    if (!Array.isArray(prev.connections)) prev.connections = [];
    prev.connections = [stepId(step)];
  }
  if (next) {
    step.next_steps = [stepId(next)];
    step.connections = [stepId(next)];
  }
  if (prev && next) {
    const prevNum = String(prev.step_number ?? prev.id);
    const nextNum = String(next.step_number ?? next.id);
    const direct = flows.find(
      (f) =>
        (String(f.source_step) === prevNum || String(f.source_step) === stepId(prev))
        && (String(f.target_step) === nextNum || String(f.target_step) === stepId(next)),
    );
    if (direct) {
      direct.target_step = stepId(step);
    }
  }
  if (prev) upsertFlow(flows, prev, stepId(step), '', false);
  if (next) upsertFlow(flows, step, stepId(next), '', false);
}

function addStakeholder(mapData: any, name: string) {
  if (!name) return;
  if (!mapData.elements) mapData.elements = {};
  const raw = mapData.elements.stakeholders;
  if (Array.isArray(raw)) {
    const exists = raw.some((s) => (typeof s === 'string' ? s : s?.name) === name);
    if (!exists) raw.push(name);
  } else if (raw && typeof raw === 'object') {
    if (!(name in raw)) raw[name] = '';
  } else {
    mapData.elements.stakeholders = { [name]: '' };
  }
  // Intentionally do NOT add a swimlane.
}

function applyProposalToExistingStep(step: any, finding: AccuracyGateFinding, overrides?: ApplyFindingOptions['overrides']) {
  const proposal = finding.proposal || {};
  const name = overrides?.name != null ? overrides.name : proposal.name;
  const description = overrides?.description != null ? overrides.description : proposal.description;
  const actor = overrides?.assignee != null ? overrides.assignee : proposal.actor;
  if (name != null && name !== '') {
    step.name = name;
  }
  if (description != null) {
    step.description = description;
    step.full_text = description;
    step.detailed_description = description;
  }
  if (actor != null) {
    step.actor = actor;
    step.assignee = actor;
    step.swimlane = actor;
  }
  const wantDecision =
    overrides?.isDecision != null
      ? overrides.isDecision
      : proposal.step_type === 'decision' || finding.fix === 'mark_decision' || finding.fix === 'set_decision_paths';
  if (finding.fix === 'classify_annotation' || proposal.step_type === 'annotation') {
    step.action_type = 'annotation';
    step.step_type = 'annotation';
    step.bpmn_type = 'annotation';
    step.classification = proposal.classification || 'annotation';
    step.is_decision = false;
    step.decision_paths = null;
    return step;
  }
  if (wantDecision || overrides?.decisionPaths) {
    applyDecisionToStep(step, overrides?.decisionPaths || proposal.decision_paths, wantDecision !== false);
  }
  return step;
}

export function applyAccuracyGateFinding(
  mapData: any,
  finding: AccuracyGateFinding,
  options: ApplyFindingOptions,
): ApplyFindingResult {
  const pendingDeleteIds = [...(options.pendingDeleteIds || [])];
  if (options.action === 'reject') {
    return { mapData, pendingDeleteIds, status: 'rejected' };
  }
  if (options.action === 'keep_flagged') {
    return { mapData, pendingDeleteIds, status: 'kept_flagged' };
  }

  if (
    requiresBothDecisionPaths(finding, !!options.sourceHasDiamonds)
    && (finding.fix === 'mark_decision' || finding.fix === 'set_decision_paths')
  ) {
    const paths = options.overrides?.decisionPaths || finding.proposal?.decision_paths;
    if (!decisionPathsComplete(paths)) {
      return {
        mapData,
        pendingDeleteIds,
        status: finding.status,
        error: 'Set both YES and NO targets before approving this decision finding.',
      };
    }
  }

  const next = cloneMap(mapData);
  const processes = processesOf(next);
  const status: FindingStatus = options.action === 'adjust' ? 'adjusted' : 'approved';
  let createdStepId: string | undefined;

  if (finding.fix === 'add_stakeholder') {
    addStakeholder(next, String(finding.proposal?.name || finding.source?.text || ''));
    return { mapData: next, pendingDeleteIds, status };
  }

  if (finding.fix === 'flag_for_sme' && options.action !== 'adjust') {
    return { mapData, pendingDeleteIds, status: 'kept_flagged', error: 'Use Keep flagged — do not invent a path to pass.' };
  }

  if (isInsertOverlayFinding(finding) || finding.fix === 'insert_step' || finding.fix === 'restore_end') {
    const proposal = finding.proposal || {};
    const stepType = proposal.step_type || (finding.fix === 'restore_end' ? 'end' : 'process');
    const overrides = options.overrides || {};
    let paths = overrides.decisionPaths || proposal.decision_paths;
    const afterId = proposal.insert_after_step_id || finding.target?.insert_after_step_id;
    const afterIdx = findIndexById(processes, afterId);
    if (paths) {
      const yes = resolveNewEnd(next, afterIdx >= 0 ? afterIdx : processes.length - 1, paths.yes);
      const no = resolveNewEnd(next, afterIdx >= 0 ? afterIdx : processes.length - 1, paths.no);
      paths = { ...paths, yes, no };
    }
    const newStep = makeManualStep({
      name: overrides.name || proposal.name || finding.source?.text || 'Step',
      description: overrides.description || proposal.description || finding.source?.quote || '',
      assignee: overrides.assignee || proposal.actor || finding.source?.lane || '',
      stepType,
      isDecision: overrides.isDecision ?? stepType === 'decision',
      decisionPaths: paths,
    });
    const insertAt = insertAfter(processesOf(next), afterId, newStep);
    if (stepType !== 'end' && stepType !== 'start' && stepType !== 'annotation') {
      wireLinearInsert(next, insertAt);
    } else if (stepType === 'end') {
      const prev = insertAt > 0 ? processesOf(next)[insertAt - 1] : null;
      if (prev && stepTypeOf(prev) !== 'end') {
        prev.next_steps = [stepId(newStep)];
        upsertFlow(flowsOf(next), prev, stepId(newStep), '', false);
      }
    }
    if (newStep.is_decision) syncDecisionSequenceFlows(next, newStep);
    createdStepId = stepId(newStep);
    return { mapData: next, pendingDeleteIds, status, createdStepId };
  }

  if (finding.fix === 'delete_step') {
    const id = finding.target?.step_id;
    const idx = findIndexById(processes, id);
    if (idx >= 0 && isBoundaryStep(processes[idx])) {
      return { mapData, pendingDeleteIds, status: finding.status, error: 'Source START/END cannot be deleted as extra.' };
    }
    if (id && !pendingDeleteIds.includes(String(id))) pendingDeleteIds.push(String(id));
    return { mapData: next, pendingDeleteIds, status };
  }

  const targetId = finding.target?.step_id || finding.target?.edge?.from;
  const idx = findIndexById(processes, targetId);
  if (idx < 0 && finding.fix !== 'rewire_edge') {
    return { mapData, pendingDeleteIds, status: finding.status, error: 'Target step is not on this map.' };
  }

  if (finding.fix === 'rewire_edge') {
    const fromId = finding.target?.edge?.from || finding.target?.step_id;
    const fromIdx = findIndexById(processes, fromId);
    const newTo = options.overrides?.decisionPaths?.yes ?? finding.proposal?.next_step_id;
    if (fromIdx >= 0) {
      const from = processes[fromIdx];
      if (newTo == null || newTo === '') {
        from.next_steps = [];
        removeFlowsFrom(flowsOf(next), from);
      } else {
        from.next_steps = [String(newTo)];
        from.connections = [String(newTo)];
        removeFlowsFrom(flowsOf(next), from);
        upsertFlow(flowsOf(next), from, String(newTo), '', false);
      }
    } else if (finding.target?.edge?.from && finding.target?.edge?.to) {
      const flows = flowsOf(next);
      for (let i = flows.length - 1; i >= 0; i -= 1) {
        const f = flows[i];
        if (String(f.source_step) === String(finding.target.edge.from) && String(f.target_step) === String(finding.target.edge.to)) {
          if (newTo == null || newTo === '') flows.splice(i, 1);
          else f.target_step = String(newTo);
        }
      }
    }
    return { mapData: next, pendingDeleteIds, status };
  }

  const step = processes[idx];
  if (finding.fix === 'classify_annotation') {
    const outgoing = Array.isArray(step.next_steps) && step.next_steps[0]
      ? String(step.next_steps[0])
      : (flowsOf(next).find((f) => flowMatchesSource(f, step))?.target_step || null);
    applyProposalToExistingStep(step, finding, options.overrides);
    step.next_steps = [];
    step.connections = [];
    removeFlowsFrom(flowsOf(next), step);
    retargetIncoming(flowsOf(next), stepId(step), outgoing ? String(outgoing) : null);
    return { mapData: next, pendingDeleteIds, status };
  }

  let paths = options.overrides?.decisionPaths || finding.proposal?.decision_paths;
  if (paths) {
    const yes = resolveNewEnd(next, idx, paths.yes);
    const no = resolveNewEnd(next, idx, paths.no);
    paths = { ...paths, yes, no };
  }
  applyProposalToExistingStep(step, finding, {
    ...options.overrides,
    decisionPaths: paths || options.overrides?.decisionPaths,
  });
  if (step.is_decision) syncDecisionSequenceFlows(next, step);
  return { mapData: next, pendingDeleteIds, status, createdStepId };
}

export function applyManualDecisionEdit(
  mapData: any,
  stepIndex: number,
  fields: { isDecision: boolean; decisionPaths?: DecisionPathsProposal },
): any {
  const next = cloneMap(mapData);
  const processes = processesOf(next);
  const step = processes[stepIndex];
  if (!step) return next;
  let paths = fields.decisionPaths;
  if (fields.isDecision && paths) {
    const yes = resolveNewEnd(next, stepIndex, paths.yes);
    const no = resolveNewEnd(next, stepIndex, paths.no);
    paths = { ...paths, yes, no };
  }
  applyDecisionToStep(step, paths, fields.isDecision);
  if (fields.isDecision) syncDecisionSequenceFlows(next, step);
  else {
    const nextStep = processes[stepIndex + 1];
    removeFlowsFrom(flowsOf(next), step);
    if (nextStep) upsertFlow(flowsOf(next), step, stepId(nextStep), '', false);
  }
  return next;
}
